import React, { useEffect, useMemo, useState } from "react";
import ReactSelect, {
  SingleValue,
  StylesConfig,
  type CSSObjectWithLabel,
} from "react-select";
import { CheckIcon } from "lucide-react";
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  List,
} from "react-virtualized";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { CustomSelectNewItem, CustomSelectNewProps } from "./type";
import { Icons } from "@/assets/images";
import { cn } from "@/shared/utils/utils";
import {
  VIRTUALIZED_DROPDOWN_MIN_ROW_HEIGHT,
  VIRTUALIZED_DROPDOWN_MAX_HEIGHT,
  VIRTUALIZED_DROPDOWN_OVERSCAN,
} from "@/shared/constants/virtualizedDefaultValues";

const getSearchableStyles = (
  allowInactiveSelection: boolean,
): StylesConfig<CustomSelectNewItem, false> => ({
  option: (provided, state): CSSObjectWithLabel => {
    const data = state.data as CustomSelectNewItem;
    const isInactiveOption = data.isActive === false;
    const isDisabledOption =
      (!allowInactiveSelection && isInactiveOption) || data.isDisabled === true;
    const optionBackgroundColor =
      state.isFocused || state.isSelected ? "var(--bg-hover)" : "var(--white)";

    return {
      ...provided,
      backgroundColor:
        isInactiveOption && state.isFocused
          ? "transparent"
          : optionBackgroundColor,
      color: "var(--foreground)",
      borderLeft: isInactiveOption
        ? "3px solid var(--color-destructive)"
        : undefined,
      opacity: isDisabledOption ? 0.6 : provided.opacity,
      cursor: isDisabledOption ? "not-allowed" : "pointer",
      border: isDisabledOption && !isInactiveOption ? "0" : provided.border,
      whiteSpace: "normal",
      wordBreak: "break-word",
    };
  },
  valueContainer: (base): CSSObjectWithLabel => ({
    ...base,
    overflow: "hidden",
    flexWrap: "nowrap",
    minWidth: 0,
  }),
  placeholder: (base): CSSObjectWithLabel => ({
    ...base,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
  }),
  singleValue: (base): CSSObjectWithLabel => ({
    ...base,
    color: "var(--foreground)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }),
  input: (base): CSSObjectWithLabel => ({
    ...base,
    color: "var(--foreground)",
  }),
  menuPortal: (base): CSSObjectWithLabel => ({
    ...base,
    zIndex: 999,
    pointerEvents: "auto",
  }),
  menu: (base): CSSObjectWithLabel => ({
    ...base,
    minWidth: 220,
  }),
  menuList: (base): CSSObjectWithLabel => ({
    ...base,
    maxHeight: 200,
    overflowY: "auto",
    overflowX: "hidden",
  }),
});

const CustomSelect = (props: CustomSelectNewProps): React.ReactElement => {
  const {
    items,
    defaultValue,
    value,
    onChange,
    onValueChange,
    className = "",
    labelName,
    required,
    readOnly,
    placeholder = "Select an option",
    errors,
    contentCss,
    scrollIntoViewOnOpen = false,
    isClearable = false,
    isSearchable = true,
    blurInputOnSelect = false,
    enableVirtualization = false,
    usePortal = true,
    allowInactiveSelection = false,
  } = props;
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleWheel = (event: React.WheelEvent<HTMLDivElement>): void => {
    if (!isSearchable || !usePortal || !isOpen) {
      return;
    }

    event.stopPropagation();
  };

  // const handleSearchableWrapperRef = React.useCallback(
  //   (node: HTMLDivElement | null) => {
  //     if (!node) {
  //       return;
  //     }

  //     setMenuPortalTarget(document.body);
  //     setMenuPosition("fixed");
  //   },
  //   [],
  // );

  const transformedItems = items.map((item, index) => ({
    ...item,
    isDisabled:
      item.isDisabled === true ||
      (!allowInactiveSelection && item.isActive === false),
    internalValue: `__option_${index}__`,
    originalValue: item.value ?? "",
  }));

  const getItemClassName = (item: (typeof transformedItems)[number]): string =>
    `px-3 py-0 leading-10 hover:cursor-pointer hover:bg-bg-hover ${
      item.isActive === false && !allowInactiveSelection
        ? "inactive-option"
        : ""
    } ${item.isDisabled ? "disable-option" : ""}`;

  const selectedItem = transformedItems.find(
    (item) => item.originalValue === value,
  );

  const internalValue = selectedItem?.internalValue ?? "";
  const selectedLabel = selectedItem?.label ?? "";
  const selectedIndex = transformedItems.findIndex(
    (item) => item.originalValue === value,
  );

  const measurementCache = useMemo(
    () =>
      new CellMeasurerCache({
        fixedWidth: true,
        defaultHeight: VIRTUALIZED_DROPDOWN_MIN_ROW_HEIGHT,
        minHeight: VIRTUALIZED_DROPDOWN_MIN_ROW_HEIGHT,
      }),
    [],
  );

  useEffect(() => {
    measurementCache.clearAll();
  }, [items, measurementCache]);

  if (isSearchable) {
    const selectedOption = items.find((item) => item.value === value) ?? null;

    const handleReactSelectChange = (
      option: SingleValue<CustomSelectNewItem>,
    ): void => {
      (onValueChange ?? onChange)?.(option?.value ?? "");
    };

    return (
      <div
        className={`select-wrapper w-full bg-white ${className}`}
        onWheel={handleWheel}
      >
        {labelName && (
          <Label className="mb-1.5 leading-[1.215]">
            {labelName}
            {required && (
              <span className="text-destructive inline-block -ml-1">*</span>
            )}
          </Label>
        )}

        <ReactSelect<CustomSelectNewItem>
          options={items}
          value={selectedOption}
          onChange={handleReactSelectChange}
          placeholder={placeholder}
          isClearable={isClearable}
          isDisabled={readOnly}
          isSearchable={isSearchable}
          blurInputOnSelect={blurInputOnSelect}
          className="custom-multiselect"
          classNamePrefix="custom-multiselect"
          styles={getSearchableStyles(allowInactiveSelection)}
          isOptionDisabled={(option) =>
            (!allowInactiveSelection && option.isActive === false) ||
            option.isDisabled === true
          }
          formatOptionLabel={(option, { context, selectValue }) => (
            <span className="flex items-center justify-between gap-2">
              <span>{option.label}</span>
              {context === "menu" &&
                selectValue.length > 0 &&
                selectValue[0]?.value === option.value && (
                  <CheckIcon className="size-4" aria-hidden="true" />
                )}
            </span>
          )}
          menuPortalTarget={usePortal ? document.body : undefined}
          menuPosition={usePortal ? "fixed" : "absolute"}
          menuShouldBlockScroll={usePortal}
          closeMenuOnScroll={false}
          onMenuOpen={() => setIsOpen(true)}
          onMenuClose={() => setIsOpen(false)}
        />

        {errors && <p className="mt-1 text-sm text-red-500">{errors}</p>}
      </div>
    );
  }

  const handleValueChange = (val: string): void => {
    const selected = transformedItems.find(
      (item) => item.internalValue === val,
    );

    const finalValue = selected?.originalValue ?? "";

    (onValueChange ?? onChange)?.(finalValue);
  };

  const handleClear = (e: React.PointerEvent | React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    (onValueChange ?? onChange)?.("");
  };

  const handleOpenChange = (open: boolean): void => {
    setIsOpen(open);

    if (open && scrollIntoViewOnOpen) {
      wrapperRef.current?.scrollIntoView({
        block: "center",
        inline: "nearest",
      });
    }
  };

  const hasValue = Boolean(value && value !== "");
  const virtualizedListHeight = Math.min(
    transformedItems.length * VIRTUALIZED_DROPDOWN_MIN_ROW_HEIGHT,
    VIRTUALIZED_DROPDOWN_MAX_HEIGHT,
  );

  const renderVirtualizedItems = (): React.ReactElement => (
    <div
      style={{
        height: virtualizedListHeight,
        maxHeight: VIRTUALIZED_DROPDOWN_MAX_HEIGHT,
      }}
    >
      <AutoSizer disableHeight>
        {({ width }): React.ReactElement => (
          <List
            deferredMeasurementCache={measurementCache}
            height={virtualizedListHeight}
            overscanRowCount={VIRTUALIZED_DROPDOWN_OVERSCAN}
            rowCount={transformedItems.length}
            rowHeight={measurementCache.rowHeight}
            rowRenderer={({
              index,
              key,
              parent,
              style,
            }): React.ReactElement => {
              const item = transformedItems[index];

              return (
                <CellMeasurer
                  cache={measurementCache}
                  columnIndex={0}
                  key={key}
                  parent={parent}
                  rowIndex={index}
                >
                  <div style={style}>
                    <SelectItem
                      value={item.internalValue}
                      disabled={item.isDisabled}
                      className={getItemClassName(item)}
                    >
                      {item.label}
                    </SelectItem>
                  </div>
                </CellMeasurer>
              );
            }}
            scrollToIndex={isOpen && selectedIndex >= 0 ? selectedIndex : -1}
            width={Math.max(width, 0)}
          />
        )}
      </AutoSizer>
    </div>
  );

  return (
    <div
      ref={wrapperRef}
      className={`select-wrapper w-full bg-white ${className}`}
    >
      {labelName && (
        <Label className="mb-1.5 leading-[1.215]">
          {labelName}
          {required && (
            <span className="text-destructive inline-block -ml-[4px]">*</span>
          )}
        </Label>
      )}

      <Select
        value={internalValue}
        defaultValue={defaultValue}
        onValueChange={handleValueChange}
        onOpenChange={handleOpenChange}
      >
        <div className="relative">
          <SelectTrigger
            className={`border border-border data-[placeholder]:text-placeholder text-foreground ${className} ${
              readOnly
                ? "bg-input pointer-events-none focus-visible:border-border"
                : "focus-visible:border-primary"
            }`}
          >
            {enableVirtualization ? (
              <span
                className={`block truncate pr-12 ${selectedLabel ? "" : "text-placeholder"}`}
              >
                {selectedLabel || placeholder}
              </span>
            ) : (
              <SelectValue placeholder={placeholder} className="pr-12" />
            )}
          </SelectTrigger>

          {isClearable && hasValue && !readOnly && (
            <button
              type="button"
              className="absolute right-9 top-1/2 -translate-y-1/2 z-20 bg-white pr-2 cursor-pointer"
              onPointerDown={handleClear}
              onClick={(e) => e.preventDefault()}
              aria-label="Clear selection"
            >
              <img
                src={Icons.crossIcon}
                alt="clear"
                className="select-down-arrow w-3 h-3 pointer-events-none"
              />
            </button>
          )}
        </div>

        <SelectContent
          position="popper"
          className={cn(
            contentCss,
            enableVirtualization ? "overflow-hidden" : "",
          )}
        >
          {enableVirtualization ? (
            renderVirtualizedItems()
          ) : (
            <SelectGroup>
              {transformedItems.map((item) => (
                <SelectItem
                  key={item.internalValue}
                  value={item.internalValue}
                  disabled={item.isDisabled}
                  className={getItemClassName(item)}
                >
                  {item.label}
                </SelectItem>
              ))}
            </SelectGroup>
          )}
        </SelectContent>
      </Select>

      {errors && <p className="mt-1 text-sm text-red-500">{errors}</p>}
    </div>
  );
};

export default CustomSelect;
